import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const publicLinks = [
  { label: "HOME", to: "/" },
  { label: "CHI SIAMO", to: "/chi-siamo" },
  { label: "CERTIFICATO DI AGIBILITÀ", to: "/certificato-agibilita" },
  { label: "F24", to: "/f24" },
  { label: "DICHIARAZIONE MENSILE", to: "/dichiarazione-mensile" },
  { label: "NEWS", to: "/news" },
  { label: "CALCOLATORE", to: "/calcolatore" },
  { label: "CONTATTI", to: "/contatti" },
];

const userLinks = [
  { label: "AREA PERSONALE", to: "/area-personale" },
  { label: "LE MIE RICHIESTE", to: "/le-mie-richieste" },
  { label: "RICHIEDI AGIBILITÀ", to: "/richiedi-agibilita" },
  { label: "ASSISTENZA", to: "/assistenza" },
  { label: "NEWS", to: "/news" },
  { label: "CALCOLATORE", to: "/calcolatore" },
  { label: "PROFILO", to: "/profilo" },
];

export default function Header() {
  const { user, logout, isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    setOpen(false);
    navigate("/");
  };

  const links = user ? userLinks : publicLinks;

  const navClass = ({ isActive }) =>
    `nav-link block px-3 py-2 text-xs font-semibold tracking-wide transition-colors duration-200 rounded-md ${
      isActive ? "text-gold bg-white/10" : "text-white/75 hover:text-gold hover:bg-white/5"
    }`;

  return (
    <header data-testid="site-header" className="sticky top-0 z-50 border-b border-gold/30 bg-[#5E1A2C]/95 shadow-lg shadow-black/20 backdrop-blur-md">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2.5">
          <img src="/assets/emblem.png" alt="Logo ENPALSBOX" className="h-10 w-10 rounded-lg shadow-md shadow-gold/25 ring-1 ring-gold/40 transition-transform duration-300 hover:rotate-3 hover:scale-105" />
          <span className="font-heading text-xl font-bold tracking-tight text-[#FFFDF7]">
            ENPALS<span className="text-gold">BOX</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" data-testid="desktop-nav">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.to === "/"} className={navClass} data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              {l.label}
            </NavLink>
          ))}
          {isAdmin && (
            <NavLink to="/admin" className={navClass} data-testid="nav-area-admin">
              AREA ADMIN
            </NavLink>
          )}
          {user ? (
            <button onClick={handleLogout} data-testid="nav-esci" className="ml-2 rounded-lg border border-gold/60 px-4 py-2 text-xs font-bold text-gold transition-colors duration-200 hover:bg-gold hover:text-[#1F040A]">
              ESCI
            </button>
          ) : (
            <>
              <Link to="/accedi" data-testid="nav-accedi" className="ml-2 rounded-lg border border-gold/60 px-4 py-2 text-xs font-bold text-gold transition-colors duration-200 hover:bg-gold hover:text-[#1F040A]">
                ACCEDI
              </Link>
              <Link to="/iscriviti" data-testid="nav-iscriviti" className="ml-2 rounded-lg bg-gold px-4 py-2 text-xs font-bold text-[#1F040A] transition-colors duration-200 hover:bg-[#F7E5A9]">
                ISCRIVITI
              </Link>
            </>
          )}
        </nav>

        <button
          className="rounded-md p-2 text-gold lg:hidden"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <nav className="border-t border-gold/20 bg-[#5E1A2C] px-4 py-3 lg:hidden" data-testid="mobile-nav">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.to === "/"} className={navClass} onClick={() => setOpen(false)} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              {l.label}
            </NavLink>
          ))}
          {isAdmin && (
            <NavLink to="/admin" className={navClass} onClick={() => setOpen(false)} data-testid="mobile-nav-area-admin">
              AREA ADMIN
            </NavLink>
          )}
          {user ? (
            <button onClick={handleLogout} data-testid="mobile-nav-esci" className="mt-2 w-full rounded-lg border border-gold/60 px-4 py-2.5 text-xs font-bold text-gold transition-colors hover:bg-gold hover:text-[#1F040A]">
              ESCI
            </button>
          ) : (
            <div className="mt-2 flex gap-2">
              <Link to="/accedi" onClick={() => setOpen(false)} data-testid="mobile-nav-accedi" className="flex-1 rounded-lg border border-gold/60 px-4 py-2.5 text-center text-xs font-bold text-gold transition-colors hover:bg-gold hover:text-[#1F040A]">
                ACCEDI
              </Link>
              <Link to="/iscriviti" onClick={() => setOpen(false)} data-testid="mobile-nav-iscriviti" className="flex-1 rounded-lg bg-gold px-4 py-2.5 text-center text-xs font-bold text-[#1F040A] transition-colors hover:bg-[#F7E5A9]">
                ISCRIVITI
              </Link>
            </div>
          )}
        </nav>
      )}
    </header>
  );
}
